import classNames from 'classnames'
import React, {
  PropsWithChildren,
  forwardRef,
  useEffect,
  useId,
  useMemo,
  useRef,
  type ChangeEvent,
} from 'react'

import { Body1, Body2, Caption1 } from '@ui/Typography/Typography'

import { Box } from '@shared/components/Box'

import styles from './Checkbox.module.scss'

type Size = 'sm' | 'md' | 'lg'

export interface CheckboxProps extends PropsWithChildren {
  size?: Size
  checked?: boolean
  defaultChecked?: boolean
  onChange?: (next: boolean) => void
  disabled?: boolean
  intermediate?: boolean
  id?: string
  name?: string
  error?: string
  caption?: string
  autoFocus?: boolean
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  {
    size = 'lg',
    checked,
    defaultChecked,
    onChange,
    disabled,
    intermediate,
    id,
    name,
    caption,
    children,
    error,
    autoFocus,
  },
  forwardedRef
) {
  const internalRef = useRef<HTMLInputElement>(null)
  const ref = forwardedRef || internalRef

  const rid = useId()
  const inputId = id ?? `cb-${rid}`
  const captionId = caption ? `${inputId}-caption` : undefined
  const errorId = error ? `${inputId}-error` : undefined

  const describedBy = useMemo(
    () => [captionId, errorId].filter(Boolean).join(' ') || undefined,
    [captionId, errorId]
  )

  const isControlled = typeof checked === 'boolean'

  useEffect(() => {
    if (ref && 'current' in ref && ref.current) {
      ref.current.indeterminate = !!intermediate && !checked
    }
  }, [intermediate, checked, ref])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.checked)
  }

  const BodyText = () => {
    const textColor = disabled ? 'text-base-em' : 'text-high-em'

    if (!children) return null

    switch (size) {
      case 'sm':
        return <Caption1 color={textColor}>{children}</Caption1>
      case 'md':
        return <Body2 color={textColor}>{children}</Body2>
      case 'lg':
        return <Body1 color={textColor}>{children}</Body1>
    }
  }

  return (
    <label
      htmlFor={inputId}
      className={classNames(styles.checkbox, styles[`size-${size}`], {
        [styles.disabled]: disabled,
        [styles.error]: error,
      })}
      data-checkbox-label=''
    >
      <input
        ref={ref}
        id={inputId}
        name={name}
        type='checkbox'
        className={styles.input}
        {...(isControlled ? { checked } : { defaultChecked })}
        disabled={disabled}
        aria-checked={intermediate ? 'mixed' : isControlled ? checked : undefined}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        onChange={handleChange}
        autoFocus={autoFocus}
      />
      <span
        className={styles.box}
        aria-hidden
        data-intermediate={intermediate ? '' : undefined}
        data-checked={isControlled ? (checked ? '' : undefined) : undefined}
      />
      {(children || caption || error) && (
        <Box flex direction='column' gap='x2' pt={size === 'sm' ? 'x2' : undefined} pl='x8'>
          <BodyText />
          {caption && (
            <Caption1 id={captionId} color={disabled ? 'text-low-em' : 'text-med-em'}>
              {caption}
            </Caption1>
          )}
          {error && (
            <Caption1 id={errorId} color={disabled ? 'text-danger-low-em' : 'text-danger-high-em'}>
              {error}
            </Caption1>
          )}
        </Box>
      )}
    </label>
  )
})
